import { useEffect, useRef, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import { database } from "../constants/firebase";
import { ref, onValue, push, set, serverTimestamp, update } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import useMoveTo from "../constants/navigation";
import { CiSearch } from "react-icons/ci";
import { FiCamera, FiCheck, FiMinus } from "react-icons/fi";
import { ROUTES } from "../App";
import MobileHeader from "../components/MobileHeader";
import { useParams } from "react-router";
import { resizeImage } from "../utils/utils";

const CreateGroup = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const { id: groupId } = useParams(); // Get groupId from URL for editing

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [groupName, setGroupName] = useState("");
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [groupPhoto, setGroupPhoto] = useState(null);
    const [existingGroupPhoto, setExistingGroupPhoto] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [existingMembers, setExistingMembers] = useState({});
    const [isAdmin, setIsAdmin] = useState(true);
    const fileInputRef = useRef(null);


    // Check if we're in edit mode
    useEffect(() => {
        if (groupId) {
            setIsEditing(true);
            // Fetch existing group data
            const groupRef = ref(database, `Groups/${groupId}`);
            const unsubscribe = onValue(groupRef, (snapshot) => {
                if (snapshot.exists()) {
                    const groupData = snapshot.val();
                    setGroupName(groupData.name || "");
                    setExistingGroupPhoto(groupData.photo || null);
                    setExistingMembers(groupData.members || {});

                    // ✅ Check admin
                    setIsAdmin(groupData.createdBy === currentUser?.uid);

                    // Set selected users (excluding current user)
                    const memberIds = Object.keys(groupData.members || {});
                    setSelectedUsers(memberIds.filter(uid => uid !== currentUser?.uid));
                }
            });

            return () => unsubscribe();
        }
    }, [groupId, currentUser]);

    useEffect(() => {
        if (!currentUser?.uid) return;

        const usersRef = ref(database, `Users`);
        const unsubscribe = onValue(usersRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.entries(data)
                    .filter(([uid]) => uid !== currentUser.uid) // exclude self
                    .map(([uid, user]) => ({ uid, ...user }));
                setUsers(list);
            }
        });

        return () => unsubscribe();
    }, [currentUser]);

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const resizedBlob = await resizeImage(file, 200); // 200px x 200px
            setGroupPhoto(resizedBlob);
        } catch (err) {
            console.error("Image resize failed:", err);
        }
    };

    const handleSearchChange = (e) => setSearchQuery(e.target.value);

    const toggleSelectUser = (uid) => {
        setSelectedUsers((prev) =>
            prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
        );
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim() || selectedUsers.length === 0) {
            alert("Please enter a group name and select at least one member.");
            return;
        }

        setIsSubmitting(true);

        try {
            const members = [...selectedUsers, currentUser.uid];
            let photoURL = existingGroupPhoto;

            // 👉 Upload new group photo if provided
            if (groupPhoto) {
                const storage = getStorage();
                const fileName = `groupPhotos/${groupId || 'new'}_${Date.now()}.webp`;
                const photoRef = storageRef(storage, fileName);

                await new Promise((resolve, reject) => {
                    const uploadTask = uploadBytesResumable(photoRef, groupPhoto);

                    uploadTask.on(
                        "state_changed",
                        null,
                        reject,
                        async () => {
                            photoURL = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve();
                        }
                    );
                });
            }

            if (isEditing) {

                // Update existing group
                const groupData = {
                    name: groupName,
                    photo: photoURL,
                    members: {
                        ...existingMembers,
                        ...members.reduce((acc, uid) => {
                            acc[uid] = true;
                            return acc;
                        }, {})
                    }
                };

                await update(ref(database, `Groups/${groupId}`), groupData);
            } else {
                // Create new group
                const groupsRef = ref(database, "Groups");
                const newGroupRef = push(groupsRef);

                const groupData = {
                    groupId: newGroupRef.key,
                    name: groupName,
                    createdBy: currentUser.uid,
                    createdAt: serverTimestamp(),
                    members: members.reduce((acc, uid) => {
                        acc[uid] = true;
                        return acc;
                    }, {}),
                    photo: photoURL,
                };

                // Save group
                await set(newGroupRef, groupData);

                // Add reference under current user → hostedGroups
                const userGroupRef = ref(database, `Users/${currentUser.uid}/hostedGroups/${newGroupRef.key}`);
                await set(userGroupRef, true);
            }

            setIsSubmitting(false);
            navigate(ROUTES.CHATS, { state: { incommingTab: "Groups" } });
        } catch (error) {
            console.error("❌ Error creating/updating group:", error);
            alert("Failed to create/update group. Please try again.");
            setIsSubmitting(false);
        }
    };

    const handleRemoveMember = async (uidToRemove) => {
        // ❌ Admin can't remove himself (optional rule)
        if (isAdmin && uidToRemove === currentUser.uid) {
            window.showToast("Admin cannot remove himself.", "error");
            return;
        }

        // ❌ Member can only remove himself
        if (!isAdmin && uidToRemove !== currentUser.uid) {
            return;
        }

        const confirmMsg =
            uidToRemove === currentUser.uid
                ? "Are you sure you want to leave this group?"
                : "Are you sure you want to remove this member?";

        if (!window.confirm(confirmMsg)) return;

        try {
            const updates = {};
            updates[`Groups/${groupId}/members/${uidToRemove}`] = null;

            await update(ref(database), updates);

            // 🧹 Optional: remove group from user's list
            await update(
                ref(database, `Users/${uidToRemove}/groups/${groupId}`),
                null
            );

        } catch (error) {
            console.error("Failed to remove member:", error);
            // alert("Failed to remove member");
        }
    };

    // Filter users by search
    const filteredUsers = users.filter((u) =>
        u.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            {/* Header */}
            <MobileHeader title={isEditing ? "Edit Group" : "Create Group"} isBackBtn={true} />

            <div className="flex">
                <Sidebar />

                <main className="flex-1 h-screen pt-10 pb-28 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white relative">
                    {/* Top Bar */}
                    <div className="flex items-center justify-between pr-6">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img
                                src={ICONS.arrowBack}
                                className="w-4 h-4 md:w-5 md:h-5 brightness-0 dark:invert"
                                alt="Back"
                            />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            {isEditing ? "Edit Group" : "Create Group"}
                        </h2>
                        <div />
                    </div>
                    <div className="max-w-md mx-auto md:pt-10 pt-3 space-y-6">
                        {/* Group Photo */}
                        <div className="flex flex-col items-center">
                            <button
                                onClick={() => fileInputRef.current.click()}
                            >
                                <div
                                    className="w-32 h-32 rounded-full dark:bg-gray-700 bg-gray-300 flex items-center justify-center cursor-pointer overflow-hidden"
                                >
                                    {groupPhoto ? (
                                        <img
                                            src={URL.createObjectURL(groupPhoto)}
                                            alt="Group"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : existingGroupPhoto ? (
                                        <img
                                            src={existingGroupPhoto}
                                            alt="Group"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <FiCamera className="text-3xl dark:text-gray-400 text-gray-700" />
                                    )}
                                </div>
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageChange}
                                accept="image/*"
                                className="hidden"
                            />
                            <p className="text-sm dark:text-gray-400 text-gray-700 mt-2">
                                {isEditing ? "Change Group photo" : "Add Group photo"}
                            </p>
                        </div>

                        {/* Group Name */}
                        <div className="mt-6">
                            <input
                                type="text"
                                value={groupName}
                                disabled={!isAdmin}
                                onChange={(e) => setGroupName(e.target.value)}
                                placeholder="Enter group name..."
                                className={`w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-[#333] bg-[#F5F9FE] dark:bg-white text-black dark:text-[#262626] outline-none
                                     ${!isAdmin ? "opacity-50 cursor-not-allowed" : ""}`}
                            />
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full md:w-full mt-6">
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="rounded-xl pl-5 pr-10 py-3 bg-[#F5F9FE] dark:bg-white placeholder-[#6b7280] outline-none w-full text-[#262626] dark:text-[#262626] border border-gray-200 dark:border-none"
                            />
                            <CiSearch className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-500 text-2xl" />
                        </div>

                        {/* User List */}
                        <div className="mt-6">
                            <div className="max-h-96 overflow-y-auto space-y-4 pr-2">
                                {filteredUsers.length === 0 ? (
                                    <div className="text-center text-gray-500 py-6">
                                        No users found
                                    </div>
                                ) : (
                                    filteredUsers.map((user) => {
                                        const isSelected = selectedUsers.includes(user.uid);
                                        const isExistingMember = existingMembers[user.uid];

                                        return (
                                            <div
                                                key={user.uid}
                                                className={`flex items-center justify-between py-3 px-4 border rounded-xl border-gray-200 dark:border-[#2A2A2A] hover:bg-[#f2f2f2] dark:hover:bg-[#2a2a2a] text-black dark:text-white ${isExistingMember ? 'opacity-90' : ''
                                                    }    ${!isAdmin ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} `}
                                                onClick={() => {
                                                    if (!isAdmin) return;
                                                    if (!isExistingMember) toggleSelectUser(user.uid);
                                                }}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={
                                                            user.profilePic ||
                                                            'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef'
                                                        }
                                                        alt={user.name}
                                                        className="w-10 h-10 rounded-full object-cover"
                                                    />
                                                    <div>
                                                        <p className="font-medium">{user.name || "Unnamed"}</p>
                                                        {isExistingMember && (
                                                            <p className="text-xs text-gray-500">Current member</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Selection check */}
                                                {!isExistingMember ? (
                                                    <div
                                                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected
                                                            ? "bg-[#327691] border-[#327691] text-white"
                                                            : "border-gray-400"
                                                            }`}
                                                    >
                                                        {isSelected && <FiCheck size={16} />}
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveMember(user.uid);
                                                        }}
                                                        disabled={!isAdmin && user.uid !== currentUser.uid}
                                                    >
                                                        <div
                                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center bg-red-500 border-red-500 text-white`}
                                                        >
                                                            {isSelected && <FiMinus size={16} />}
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <button
                            onClick={handleCreateGroup}
                            disabled={isSubmitting}
                            className="bg-primary text-white text-lg font-semibold py-3 rounded-xl w-full mt-4"
                        >
                            {isSubmitting
                                ? (isEditing ? "Updating..." : "Creating...")
                                : (isEditing ? isAdmin ? "Update Group" : 'Update Photo' : "Create Group")
                            }
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default CreateGroup;